import {isValidationFailedResponse, ValidationFailures} from '@/api';
import {FeedFile} from '@/api/models/FeedFiles.ts';
import {Feed, Feeds} from '@/api/models/Feeds.ts';
import FeedFileSelect from '@/components/Selects/FeedFileSelect.tsx';
import ValidatedFormGroup from '@/components/ValidatedFormGroup.tsx';
import {Toaster, ToastErrors} from '@/toaster.ts';
import {Button, Classes, Dialog, InputGroup} from '@blueprintjs/core';
import {ChangeEventHandler, EventHandler, ReactElement, SyntheticEvent, useCallback, useState} from 'react';
import {useNavigate} from 'react-router';

interface Props {
	feed: Feed,
	onClose: () => void,
}

export default function CloneDialog({onClose, feed}: Props): ReactElement {
	const navigate = useNavigate();

	const [processing, setProcessing] = useState(false);

	const [name, setName] = useState('');
	const onNameChange: ChangeEventHandler<HTMLInputElement> = useCallback(event => {
		setName(event.currentTarget.value);
	}, []);

	const [file, setFile] = useState<FeedFile | null>(null);

	const [validationFailures, setValidationFailures] = useState<ValidationFailures | null>(null);

	const onSubmit: EventHandler<SyntheticEvent> = useCallback(async event => {
		event.preventDefault();

		if (processing)
			return;
		else if (!file || name.length === 0)
			throw new Error('Cannot call onSubmit() without a name and file');

		setProcessing(true);

		let cloned: Feed;

		try {
			cloned = await Feeds.clone(feed.id, {
				name,
				file: file.id,
			});
		} catch (error) {
			if (isValidationFailedResponse(error)) {
				Toaster.error(error.error.message);
				setValidationFailures(error.error.context.failures);
			} else
				ToastErrors.fromError(error);

			return;
		} finally {
			setProcessing(false);
		}

		Toaster.success(`Successfully cloned ${feed.name}. You will be redirected to the new feed.`);
		navigate(`/feeds/${cloned.id}`);
	}, [onClose, processing, file, name]);

	return (
		<Dialog isOpen={true} title="Copy Feed" onClose={onClose}>
			<div className={Classes.DIALOG_BODY}>
				<ValidatedFormGroup label="Name" labelFor="name" failures={validationFailures}>
					<InputGroup name="name" value={name} onChange={onNameChange} />
				</ValidatedFormGroup>

				<ValidatedFormGroup label="Source File" labelFor="file" failures={validationFailures}>
					<FeedFileSelect current={file} onChange={setFile} />
				</ValidatedFormGroup>
			</div>

			<div className={Classes.DIALOG_FOOTER}>
				<div className={Classes.DIALOG_FOOTER_ACTIONS}>
					<Button text="Cancel" onClick={onClose} disabled={processing} />

					<Button
						text="Submit"
						intent="primary"
						onClick={onSubmit}
						disabled={!file || name.length === 0}
						loading={processing}
					/>
				</div>
			</div>
		</Dialog>
	);
}
